import { useState, useMemo, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import Select, { type GroupBase, type SingleValue } from 'react-select'
import type { DropdownIndicatorProps } from 'react-select/dist/declarations/src/components/indicators'
import { LocationService } from '../../services/location.service'
import { useQuery } from '@tanstack/react-query'
import debounce from 'lodash/debounce'
import style from './SearchLocation.module.scss'

export interface Option {
  label: string
  value: string
}

export interface SearchLocationProps {
  name: string
  defaultValue?: string
  placeholder?: string
  noOptionsMessage?: string
}

const SearchLocation = ({
  name,
  defaultValue,
  placeholder = 'Search location',
  noOptionsMessage = 'Please enter location',
}: SearchLocationProps) => {
  const { setValue } = useFormContext()
  const locationService = LocationService.getInstance()
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [selectedValue, setSelectedValue] = useState<Option | undefined>(
    defaultValue
      ? {
          label: defaultValue,
          value: defaultValue,
        }
      : undefined,
  )

  // Debounced search function
  const debouncedSetSearch = useMemo(() => debounce((term: string) => setSearchTerm(term), 300), [])

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel()
    }
  }, [debouncedSetSearch])

  // React Query for fetching locations
  const { data: locations, isLoading } = useQuery({
    queryKey: ['locations', searchTerm],
    queryFn: async () => {
      if (!searchTerm.trim()) return []
      const response = await locationService.getLocation(searchTerm)
      return response.data.places.map((place: string) => ({
        label: place,
        value: place,
      }))
    },
    enabled: !!searchTerm.trim(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const handleViewClickCallBack = (option: SingleValue<Option>) => {
    if (!option) return

    setSelectedValue(option)
    setValue(name, option.value)
  }

  const handleInputChange = (inputValue: string) => {
    debouncedSetSearch(inputValue)
  }

  return (
    <Select
      placeholder={placeholder}
      className="select-option"
      classNamePrefix="select-option"
      options={locations || []}
      isLoading={isLoading}
      onInputChange={handleInputChange}
      onChange={handleViewClickCallBack}
      value={selectedValue}
      blurInputOnSelect={false}
      components={{
        DropdownIndicator: props => {
          return <CustomDropdownIndicator {...props} />
        },
      }}
      noOptionsMessage={() => noOptionsMessage}
    />
  )
}

export default SearchLocation

const CustomDropdownIndicator = (
  props: DropdownIndicatorProps<Option, false, GroupBase<Option>>,
) => {
  return (
    <div className={`${style.customDropdownIndicator} ${props.isFocused && style.isFocused}`} />
  )
}
